export function downloadFile(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function generateFilename(prefix: string, extension: string): string {
  const now = new Date();
  const date = now.toISOString().split('T')[0];
  const time = now.toTimeString().split(' ')[0]?.replace(/:/g, '-') ?? '';
  return `${prefix}-${date}-${time}.${extension}`;
}

export function downloadNamedDataUrl(dataUrl: string, prefix: string, extension: string): void {
  downloadFile(dataUrl, generateFilename(prefix, extension));
}

export function downloadNamedSvg(svgString: string, prefix: string): void {
  downloadSVG(svgString, generateFilename(prefix, 'svg'));
}

export function createBlankCanvasDataUrl(
  width: number,
  scale: number,
  backgroundColor?: string,
  height = width,
  quality?: number
): string {
  const outputCanvas = document.createElement('canvas');
  outputCanvas.width = width * scale;
  outputCanvas.height = height * scale;
  const outputCtx = outputCanvas.getContext('2d');
  if (outputCtx && backgroundColor) {
    outputCtx.fillStyle = backgroundColor;
    outputCtx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
  }
  return quality === undefined
    ? outputCanvas.toDataURL('image/png')
    : outputCanvas.toDataURL('image/png', quality);
}

export function createScaledCanvasContext(
  width: number,
  height: number,
  options: {
    scale?: number;
    backgroundColor?: string;
    translate?: { x: number; y: number };
  } = {}
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  const scale = options.scale ?? 1;
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  if (options.backgroundColor) {
    ctx.fillStyle = options.backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  if (scale !== 1) {
    ctx.scale(scale, scale);
  }
  if (options.translate) {
    ctx.translate(options.translate.x, options.translate.y);
  }

  return { canvas, ctx };
}

export function buildScaledCanvasOptions(
  scale: number,
  translate: { x: number; y: number },
  backgroundColor?: string
): { scale: number; translate: { x: number; y: number }; backgroundColor?: string } {
  const options: {
    scale: number;
    translate: { x: number; y: number };
    backgroundColor?: string;
  } = {
    scale,
    translate,
  };
  if (backgroundColor !== undefined) {
    options.backgroundColor = backgroundColor;
  }
  return options;
}

export type ExportBounds = { minX: number; minY: number; maxX: number; maxY: number };

export type ExportBoundsInput =
  | ExportBounds
  | { minX: number; minY: number; width: number; height: number }
  | null;

export type SvgExportConfig = {
  padding: number;
  darkColor?: string;
  fallbackSize?: { width: number; height: number };
  viewBoxOnEmpty?: boolean;
  backgroundMode?: 'absolute' | 'percent';
};

type RasterExportLayer = { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D };

type RasterExportFrame = {
  width: number;
  height: number;
  translate: { x: number; y: number };
};

type RasterExportContext = {
  frame: RasterExportFrame;
  output: RasterExportLayer;
  createLayer: () => RasterExportLayer | null;
};

type RasterExportOptions = {
  bounds: ExportBounds | null;
  padding: number;
  scale: number;
  backgroundColor?: string | undefined;
  emptySize?: number;
  render: (context: RasterExportContext) => void;
};

type ExportBackgroundOptions = {
  transparent: boolean;
  inverted: boolean;
  lightColor?: string;
  darkColor?: string;
};

type SvgBackgroundMode = 'absolute' | 'percent';

type SvgMarkupOptions = {
  width: number;
  height: number;
  viewBox?: { width: number; height: number } | null;
  backgroundColor?: string | undefined;
  backgroundMode?: SvgBackgroundMode;
  content: string;
  newline?: string;
};

type SvgExportFrame = {
  width: number;
  height: number;
  translate: { x: number; y: number };
};

export function buildBoundsFrame(
  bounds: ExportBounds,
  padding: number
): { width: number; height: number; translate: { x: number; y: number } } {
  return {
    width: bounds.maxX - bounds.minX + padding * 2,
    height: bounds.maxY - bounds.minY + padding * 2,
    translate: { x: -bounds.minX + padding, y: -bounds.minY + padding },
  };
}

export function buildSvgExportFrame(options: {
  bounds: ExportBoundsInput;
  padding: number;
  fallbackSize: { width: number; height: number };
  viewBoxOnEmpty?: boolean;
}): {
  frame: SvgExportFrame;
  viewBox: { width: number; height: number } | null;
  hasBounds: boolean;
} {
  const normalized = normalizeExportBounds(options.bounds);
  if (normalized) {
    const frame = buildBoundsFrame(normalized, options.padding);
    return {
      frame,
      viewBox: { width: frame.width, height: frame.height },
      hasBounds: true,
    };
  }

  const frame = {
    width: options.fallbackSize.width,
    height: options.fallbackSize.height,
    translate: { x: 0, y: 0 },
  };

  return {
    frame,
    viewBox: options.viewBoxOnEmpty ? { width: frame.width, height: frame.height } : null,
    hasBounds: false,
  };
}

export function normalizeExportBounds(bounds: ExportBoundsInput): ExportBounds | null {
  if (!bounds) return null;
  if ('maxX' in bounds && 'maxY' in bounds) {
    return bounds;
  }
  return {
    minX: bounds.minX,
    minY: bounds.minY,
    maxX: bounds.minX + bounds.width,
    maxY: bounds.minY + bounds.height,
  };
}

export function renderExportDataUrl(options: {
  bounds: ExportBoundsInput;
  padding: number;
  scale?: number;
  background: ExportBackgroundOptions;
  emptySize?: number;
  render: (context: RasterExportContext) => void;
}): string {
  const bounds = normalizeExportBounds(options.bounds);
  const backgroundColor = resolveExportBackgroundColor(options.background);

  const rasterOptions: RasterExportOptions = {
    bounds,
    padding: options.padding,
    scale: options.scale ?? 1,
    backgroundColor,
    render: options.render,
  };
  if (options.emptySize !== undefined) {
    rasterOptions.emptySize = options.emptySize;
  }

  return renderRasterExportDataUrl(rasterOptions);
}

export function renderRasterExportDataUrl(options: RasterExportOptions): string {
  const { bounds, padding, scale, backgroundColor, emptySize = 100, render } = options;

  if (!bounds) {
    return createBlankCanvasDataUrl(emptySize, scale, backgroundColor);
  }

  const frame = buildBoundsFrame(bounds, padding);
  const canvasOptions = buildScaledCanvasOptions(scale, frame.translate, backgroundColor);
  const output = createScaledCanvasContext(frame.width, frame.height, canvasOptions);
  if (!output) return '';

  const createLayer = (): RasterExportLayer | null =>
    createScaledCanvasContext(frame.width, frame.height, canvasOptions);

  render({ frame, output, createLayer });

  return output.canvas.toDataURL('image/png');
}

export function buildSvgMarkup(options: SvgMarkupOptions): string {
  const {
    width,
    height,
    viewBox,
    backgroundColor,
    backgroundMode = 'absolute',
    content,
    newline = '\n',
  } = options;
  const viewBoxAttr = viewBox ? ` viewBox="0 0 ${viewBox.width} ${viewBox.height}"` : '';
  const backgroundRect = backgroundColor
    ? backgroundMode === 'percent'
      ? `  <rect width="100%" height="100%" fill="${backgroundColor}"/>${newline}`
      : `  <rect width="${width}" height="${height}" fill="${backgroundColor}"/>${newline}`
    : '';

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"${viewBoxAttr}>${newline}${backgroundRect}${content}</svg>`;
}

export function resolveExportBackgroundColor({
  transparent,
  inverted,
  lightColor = '#ffffff',
  darkColor = '#000000',
}: {
  transparent: boolean;
  inverted: boolean;
  lightColor?: string;
  darkColor?: string;
}): string | undefined {
  if (transparent) return undefined;
  return inverted ? darkColor : lightColor;
}

export function svgToDataUrl(svgString: string): string {
  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  return URL.createObjectURL(blob);
}

export function downloadSVG(svgString: string, filename: string): void {
  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  downloadFile(url, filename);
  URL.revokeObjectURL(url);
}
